/**
 =========================================================
 * Material Dashboard 2 React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useCurrentUser} from "../../auth/CurrentUserProvider";

import UserMenu from "./UserMenu";
import Breadcrumbs from "../Breadcrumbs";
import LanguageSelector from "../LanguageMenu";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";

// Material Dashboard 2 React components
import MDBox from "materialDashboard/components/MDBox";
import IconButton from "@mui/material/IconButton";

// Custom styles for DashboardNavbar
import {
    navbar,
    navbarContainer,
    navbarRow,
    navbarIconButton,
} from "materialDashboard/examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
    useMaterialUIController,
    setTransparentNavbar,
    setMiniSidenav,
    setOpenSidenav,
} from "materialDashboard/context";
import {useMatches, useNavigate} from "react-router-dom";


function Navbar({ absolute, light, isMini }) {
    const navigate = useNavigate();
    let matches = useMatches();
    const [navbarType, setNavbarType] = useState();
    const [controller, dispatch] = useMaterialUIController();
    const {miniSidenav, openSidenav, fixedSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;

    const [userAnchorEl, setUserAnchorEl] = useState(false);

    const userMenuOpen = Boolean(userAnchorEl);
    const {currentUser: userQuery} = useCurrentUser()

    const handleOpenUserMenu = (event) => {
        setUserAnchorEl(event.currentTarget);
    }

    const handleCloseUserMenu = () => {
        setUserAnchorEl(null);
    }

    const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
    const handleOpenSidenav = () => setOpenSidenav(dispatch, !openSidenav);

    useEffect(() => {
        // Setting the navbar type
        if (fixedNavbar) {
            setNavbarType("sticky");
        } else {
            setNavbarType("static");
        }

        // A function that sets the transparent state of the navbar.
        function handleTransparentNavbar() {
            setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
        }

        /**
         The event listener that's calling the handleTransparentNavbar function when
         scrolling the window.
         */
        window.addEventListener("scroll", handleTransparentNavbar);

        // Call the handleTransparentNavbar function to set the state with the initial value.
        handleTransparentNavbar();

        // Remove event listener on cleanup
        return () => window.removeEventListener("scroll", handleTransparentNavbar);
    }, [dispatch, fixedNavbar]);


    // Styles for the navbar icons
    const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
        color: () => {
            let colorValue = light || darkMode ? white.main : dark.main;

            if (transparentNavbar && !light) {
                colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
            }

            return colorValue;
        },
    });

    return (
        <AppBar
            position={absolute ? "absolute" : navbarType}
            color="black"
            sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
        >
            <Toolbar sx={(theme) => navbarContainer(theme)}>
                <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
                    {
                        matches.length >= 3 ?
                            <IconButton onClick={() => navigate(-1)}>
                                <Icon sx={iconsStyle} fontSize="medium">
                                    arrow_back
                                </Icon>
                            </IconButton>
                            : <IconButton onClick={() => navigate('/')}>
                                <Icon sx={iconsStyle} fontSize="medium">
                                    home
                                </Icon>
                            </IconButton>
                    }
                    <Breadcrumbs icon="home" light={light} />
                </MDBox>
                {isMini ? null : (
                    <Stack justifyContent="flex-end" direction="row">
                        {
                            fixedSidenav ?
                                <IconButton
                                    size="small"
                                    disableRipple
                                    color="inherit"
                                    sx={navbarIconButton}
                                    onClick={handleMiniSidenav}
                                >
                                    <Icon sx={iconsStyle} fontSize="medium">
                                        {miniSidenav ? "menu" : "menu_open"}
                                    </Icon>
                                </IconButton>
                                :
                                <IconButton
                                    size="small"
                                    disableRipple
                                    color="inherit"
                                    sx={navbarIconButton}
                                    onClick={handleOpenSidenav}
                                >
                                    <Icon sx={iconsStyle} fontSize="medium">
                                        menu
                                    </Icon>
                                </IconButton>
                        }
                        <LanguageSelector iconsStyle={iconsStyle}/>

                        <MDBox color={light ? "white" : "inherit"}>
                            <IconButton
                                size="small"
                                disableRipple
                                color="inherit"
                                sx={navbarIconButton}
                                aria-controls="notification-menu"
                                aria-haspopup="true"
                                variant="contained"
                                onClick={handleOpenUserMenu}
                            >
                                <Icon sx={iconsStyle}>account_circle</Icon>
                            </IconButton>
                            <UserMenu anchorEl={userAnchorEl}
                                      open={userMenuOpen}
                                      onClose={handleCloseUserMenu}
                                      user={{
                                          name: userQuery.data?.name ?? "",
                                          surname: userQuery.data?.surname ?? "",
                                          role: userQuery.data?.role
                                      }}/>
                        </MDBox>
                    </Stack>
                )}
            </Toolbar>
        </AppBar>
    );
}

// Setting default values for the props of DashboardNavbar
Navbar.defaultProps = {
    absolute: false,
    light: false,
    isMini: false,
};

// Typechecking props for the DashboardNavbar
Navbar.propTypes = {
    absolute: PropTypes.bool,
    light: PropTypes.bool,
    isMini: PropTypes.bool,
};

export default Navbar;